import { useEffect, useRef } from 'react';

const STATS = [
  { value: '19+', label: 'Years Experience' },
  { value: '5,000+', label: 'Landscapes Transformed' },
  { value: '35+', label: 'Five-Star Reviews' },
  { value: '100%', label: 'Satisfaction Rate' },
];

export default function HeroSection() {
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    const onScroll = () => {
      el.style.transform = `translateY(${window.scrollY * 0.35}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
      {/* Parallax background */}
      <div
        ref={bgRef}
        className="absolute inset-0 w-full scale-110 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url('https://brainardsgreenscapes.com/assets/images/Vision.jpeg')`,
        }}
      />

      {/* Multi-layer overlays for cinematic depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/75 via-charcoal-900/40 to-charcoal-900/80" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/50 via-transparent to-charcoal-900/20" />

      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-400/40 to-transparent" />

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center w-full" style={{ paddingTop: '90px' }}>
        <div className="w-full max-w-7xl mx-auto px-8 py-20">
          <div className="max-w-4xl">
            {/* Eyebrow */}
            <div
              className="flex items-center gap-3 mb-8 opacity-0 animate-fade-in"
              style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
            >
              <div className="w-8 h-px bg-stone-400" />
              <span className="font-sans text-stone-300 text-xs tracking-[0.3em] uppercase">
                Premium Landscape Design &amp; Outdoor Construction
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-serif text-white font-semibold leading-[1.05] mb-8 opacity-0 animate-fade-up"
              style={{ fontSize: 'clamp(52px, 7vw, 90px)', animationDelay: '0.5s', animationFillMode: 'forwards' }}
            >
              Elevated Outdoor<br />
              <em className="italic" style={{ color: '#c4a882' }}>Living.</em> Designed<br />
              to Impress.<br />
              <span className="text-stone-300">Built to Last.</span>
            </h1>

            {/* Subheadline */}
            <p
              className="font-sans text-stone-200 text-xl leading-relaxed max-w-xl mb-12 opacity-0 animate-fade-up"
              style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}
            >
              Brainard's Greenscapes delivers premium landscape design, outdoor construction, and full-property transformations for homeowners who expect exceptional results.
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 opacity-0 animate-fade-up"
              style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}
            >
              <a
                href="#consultation"
                onClick={(e) => { e.preventDefault(); document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="group inline-flex items-center gap-3 bg-forest-500 hover:bg-forest-400 text-white font-sans font-semibold text-base px-9 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ paddingTop: '16px', paddingBottom: '16px' }}
              >
                Request a Design Consultation
                <div className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line" />
                </div>
              </a>
              <a
                href="tel:5756358883"
                className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 text-white font-sans font-medium text-base px-8 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ paddingTop: '16px', paddingBottom: '16px' }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-stone-300" />
                </div>
                Call Now: (575) 635-8883
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating stats bar */}
      <div
        className="relative z-10 border-t border-white/10 backdrop-blur-sm bg-charcoal-900/50 opacity-0 animate-fade-up"
        style={{ animationDelay: '1.1s', animationFillMode: 'forwards' }}
      >
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-4 divide-x divide-white/10">
            {STATS.map((stat) => (
              <div key={stat.label} className="py-7 px-8 flex flex-col items-center text-center">
                <span className="font-serif text-white text-3xl font-bold mb-1">{stat.value}</span>
                <span className="font-sans text-stone-400 text-xs tracking-widest uppercase">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute right-10 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3 opacity-40 z-10">
        <div className="h-16 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
        <span className="font-sans text-white text-[10px] tracking-[0.2em] uppercase" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        <div className="h-16 w-px bg-gradient-to-b from-white via-transparent to-transparent" />
      </div>
    </section>
  );
}
